import React from 'react';
import { Heart, MapPin, Clock, Camera, User, ExternalLink } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const ListingCard = ({ listing }) => {
  const formatPrice = (price) => {
    if (price === null || price === undefined) {
      return "Demander le prix";
    }
    return `${price.toLocaleString()} DH`;
  };

  const formatDate = (date) => {
    if (!date) return "Date inconnue";

    const now = new Date();
    const postedDate = new Date(date);

    // Check if date is valid
    if (isNaN(postedDate.getTime())) {
      return "Date invalide";
    }

    const diffTime = Math.abs(now - postedDate);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMinutes < 60) {
      return diffMinutes === 1 ? "il y a 1 minute" : `il y a ${diffMinutes} minutes`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? "il y a 1 heure" : `il y a ${diffHours} heures`;
    } else if (diffDays < 30) {
      return diffDays === 1 ? "il y a 1 jour" : `il y a ${diffDays} jours`;
    } else if (diffMonths < 12) {
      return diffMonths === 1 ? "il y a 1 mois" : `il y a ${diffMonths} mois`;
    } else {
      return diffYears === 1 ? "il y a 1 an" : `il y a ${diffYears} ans`;
    }
  };

  return (
    <Card className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer">
      {/* Seller Info Header */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-gray-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{listing.sellerName}</p>
              {listing.isPremium && (
                <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">Premium</span>
              )}
            </div>
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <Clock className="w-3 h-3 mr-1" />
            {formatDate(listing.datePosted)}
          </div>
        </div>
      </div>

      {/* Car Image */}
      <div className="relative">
        <img 
          src={listing.image} 
          alt={listing.title}
          className="w-full h-48 object-cover"
        />
        {listing.isNew && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
            Nouveau
          </div>
        )}
        {listing.imageCount > 1 && (
          <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded flex items-center">
            <Camera className="w-3 h-3 mr-1" />
            {listing.imageCount}
          </div>
        )}
        <button className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow">
          <Heart className="w-4 h-4 text-gray-400 hover:text-red-500 transition-colors" />
        </button>
      </div>

      {/* Listing Details */}
      <div className="p-4">
        {/* Location */}
        <div className="flex items-center text-xs text-gray-500 mb-2">
          <MapPin className="w-3 h-3 mr-1" />
          Voitures d'occasion dans {listing.location}
        </div>

        {/* Title */}
        <h3 className="font-medium text-gray-900 mb-3 line-clamp-2">{listing.title}</h3>

        {/* Specifications */}
        <div className="flex items-center space-x-4 text-xs text-gray-600 mb-3">
          <span className="bg-gray-100 px-2 py-1 rounded">{listing.year}</span>
          <span className="bg-gray-100 px-2 py-1 rounded">{listing.transmission}</span>
          <span className="bg-gray-100 px-2 py-1 rounded">{listing.fuel}</span>
        </div>

        {/* Price and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {listing.price ? (
              <div>
                <p className="text-lg font-semibold text-gray-900">{formatPrice(listing.price)}</p>
                {listing.monthlyPayment && (
                  <p className="text-xs text-gray-500">{listing.monthlyPayment} DH/mois</p>
                )}
              </div>
            ) : (
              <button className="text-sm text-blue-600 border border-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 transition-colors">
                Demander le prix
              </button>
            )}
          </div>

          {/* View Listing Button */}
          {listing.url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(listing.url, '_blank')}
              className="ml-2 flex-shrink-0"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ListingCard;